import { memo } from 'react';
import { brightenColor } from 'src/customizable-ui/dashboard/utils/brightenColor.js';

const PointsRankingCard = memo(
	({
		card,
		isEditMode = true,
		isDragging = false,
		isResizing = false,
		isHovered = false,
		userStats,
		isAdminView = false,
	}) => {
		// Sample values so admins can lay the card out before stats load; real
		// users get zeroes rather than fake numbers while the query is in flight.
		const totalPoints =
			userStats?.referralPointsTotal ?? (isAdminView ? 8128 : 0);
		const rank = userStats?.pointsRanking ?? (isAdminView ? 1 : 0);
		const cardData = card.data || {};
		const title = cardData.title || 'Points Ranking';
		const backgroundColor = cardData.backgroundColor || '#ffffff';
		const borderColor = cardData.borderColor || '#dddddd';
		const textColor = cardData.textColor || '#666666';
		const statColor = cardData.statColor || '#000000';
		const showBorder = cardData.showBorder !== false;
		const borderSize = cardData.borderSize || 1;

		const isStacked = card.height > 1;

		const getCardBorderStyle = () => {
			if (!showBorder && !isEditMode) return 'none';

			if (isDragging || isResizing) {
				return `${borderSize}px solid #74a9ffff`;
			}

			if (isEditMode && isHovered && !isDragging && !isResizing) {
				const brighterBorder = brightenColor(borderColor || '#dddddd', 30);
				return `${borderSize}px solid ${brighterBorder}`;
			}

			if (showBorder) {
				return `${borderSize}px solid ${borderColor}`;
			}

			return 'none';
		};

		const getCardShadow = () => {
			if (isDragging || isResizing) {
				return '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
			}
			if (isEditMode && isHovered && !isDragging && !isResizing) {
				return '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
			}
			return 'none';
		};

		const StatBox = ({ value, label }) => (
			<div
				className="flex flex-col items-center justify-center p-4 flex-1"
				style={{
					minHeight: isStacked ? '80px' : 'auto',
				}}
			>
				<div
					className="text-4xl font-bold mb-1"
					style={{
						color: statColor,
						fontFamily: 'Nunito Sans, sans-serif',
					}}
				>
					{value}
				</div>
				<div
					className="font-bold"
					style={{
						color: textColor,
						fontFamily: 'Nunito Sans, sans-serif',
						fontSize: '14px',
					}}
				>
					{label}
				</div>
			</div>
		);

		return (
			<div
				className="w-full h-full flex flex-col relative transition-all duration-200"
				style={{
					background: backgroundColor,
					border: getCardBorderStyle(),
					boxShadow: getCardShadow(),
					transform: isDragging ? 'scale(1.05)' : 'scale(1)',
					borderRadius: '14px',
					padding: '16px',
				}}
			>
				<div className="d-card-head">
					<h4
						style={{
							fontSize: '14px',
							fontWeight: 700,
							lineHeight: '18px',
							margin: '0 0 10px',
							color: textColor,
						}}
					>
						{title}
					</h4>
				</div>

				<div
					className={`flex ${isStacked ? 'flex-col' : 'flex-row'} gap-3 flex-1`}
					style={{
						minHeight: 0,
					}}
				>
					<StatBox value={totalPoints.toLocaleString()} label="Total Points" />
					<StatBox value={`# ${rank}`} label="Rank" />
				</div>
			</div>
		);
	}
);

PointsRankingCard.displayName = 'PointsRankingCard';

export default PointsRankingCard;
